import { SCRAPING_RUN_STATUS, type ScrapingRunSummary } from '../types';

export type ScrapingRunTone = 'neutral' | 'progress' | 'success' | 'danger' | 'warning';

const RUNNING_STATUSES = new Set<SCRAPING_RUN_STATUS>([
  SCRAPING_RUN_STATUS.PENDING,
  SCRAPING_RUN_STATUS.SUBMITTING,
  SCRAPING_RUN_STATUS.SUBMITTED,
  SCRAPING_RUN_STATUS.LIBRARIAN_PENDING,
  SCRAPING_RUN_STATUS.RUNNING_VALIDATING,
  SCRAPING_RUN_STATUS.RUNNING_PRE_SCRAPING,
  SCRAPING_RUN_STATUS.RUNNING_SUBMITTING,
  SCRAPING_RUN_STATUS.RUNNING_SCRAPING,
  SCRAPING_RUN_STATUS.RUNNING_REGISTERING_RESULT,
  SCRAPING_RUN_STATUS.RUNNING_ANALYZING,
  SCRAPING_RUN_STATUS.RUNNING_INDEXING,
]);

export function getScrapingRunStatusLabel(status: SCRAPING_RUN_STATUS) {
  switch (status) {
    case SCRAPING_RUN_STATUS.PENDING:
    case SCRAPING_RUN_STATUS.LIBRARIAN_PENDING:
      return 'Pending';
    case SCRAPING_RUN_STATUS.SUBMITTING:
    case SCRAPING_RUN_STATUS.RUNNING_SUBMITTING:
      return 'Submitting';
    case SCRAPING_RUN_STATUS.SUBMITTED:
      return 'Submitted';
    case SCRAPING_RUN_STATUS.SUBMIT_FAILED:
      return 'Submit failed';
    case SCRAPING_RUN_STATUS.CANCEL_REQUESTED:
      return 'Cancel requested';
    case SCRAPING_RUN_STATUS.CANCELED_BEFORE_SUBMIT:
      return 'Canceled before submit';
    case SCRAPING_RUN_STATUS.RUNNING_VALIDATING:
      return 'Validating';
    case SCRAPING_RUN_STATUS.RUNNING_PRE_SCRAPING:
      return 'Preparing';
    case SCRAPING_RUN_STATUS.RUNNING_SCRAPING:
      return 'Scraping';
    case SCRAPING_RUN_STATUS.RUNNING_REGISTERING_RESULT:
      return 'Registering result';
    case SCRAPING_RUN_STATUS.RUNNING_ANALYZING:
      return 'Analyzing';
    case SCRAPING_RUN_STATUS.RUNNING_INDEXING:
      return 'Indexing';
    case SCRAPING_RUN_STATUS.COMPLETED:
      return 'Completed';
    case SCRAPING_RUN_STATUS.FAILED:
      return 'Failed';
    case SCRAPING_RUN_STATUS.CANCELING:
      return 'Canceling';
    case SCRAPING_RUN_STATUS.CANCELED:
      return 'Canceled';
    default:
      return status;
  }
}

export function getScrapingRunTone(status: SCRAPING_RUN_STATUS): ScrapingRunTone {
  if (RUNNING_STATUSES.has(status)) {
    return 'progress';
  }

  if (status === SCRAPING_RUN_STATUS.COMPLETED) {
    return 'success';
  }

  if (status === SCRAPING_RUN_STATUS.FAILED || status === SCRAPING_RUN_STATUS.SUBMIT_FAILED) {
    return 'danger';
  }

  if (
    status === SCRAPING_RUN_STATUS.CANCEL_REQUESTED ||
    status === SCRAPING_RUN_STATUS.CANCELING ||
    status === SCRAPING_RUN_STATUS.CANCELED ||
    status === SCRAPING_RUN_STATUS.CANCELED_BEFORE_SUBMIT
  ) {
    return 'warning';
  }

  return 'neutral';
}

export function formatScrapingDate(value?: string | null) {
  if (value == null || value.length === 0) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: value.includes('T') ? 'short' : undefined,
  }).format(date);
}

export function toScrapingRunViewModel(run: ScrapingRunSummary) {
  return {
    id: run.run_id,
    executionDate: formatScrapingDate(run.execution_date),
    statusLabel: getScrapingRunStatusLabel(run.status),
    tone: getScrapingRunTone(run.status),
    createdAt: formatScrapingDate(run.created_at),
    completedAt: formatScrapingDate(run.completed_at),
    canceledAt: formatScrapingDate(run.canceled_at),
  };
}
